import NoteAddIcon from '@mui/icons-material/NoteAdd';
import { IconButton } from '@mui/material';
import { formatCheckCountColumn, formatCurrency } from '../utils/formatUtils';
import { NAMES_WRAP_WIDTH } from '../utils/dataUtils';

const DEFAULT_WIDTH = 75;

const STYLES = {
  header: {
    backgroundColor: '#bdbdbd',
    color: '#181818',
    borderLeft: '1px solid #fff',
    borderBottom: '1px solid #fff',
    padding: '0px 5px',
    fontSize: '.65rem',
  },
  hiddenHeader: {
    backgroundColor: '#bdbdbd',
    color: '#bdbdbd',
    borderRight: '1px solid #fff',
    borderBottom: 'none',
    padding: '2px',
    fontSize: '.65rem'
  },
  // Training names sit under the note in the same cell. They match the note's
  // size for readability and rely on the muted color, plus a divider when a note
  // is present, to read as a separate, display-only section.
  trainingNames: {
    fontSize: '.7rem',
    lineHeight: '15px',
    color: '#6b6b6b',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    // Fills the Notes cell's content box so the divider spans the full cell
    // regardless of how short the names are. Same width the height math wraps at.
    width: `${NAMES_WRAP_WIDTH}px`
  },
  // Applied to the names block only when there is a note above it to divide from.
  trainingNamesDivider: {
    borderTop: '1px solid rgba(0, 0, 0, 0.2)',
    marginTop: '2px',
    paddingTop: '2px'
  }
};

const createBaseColumn = (accessor, header, width = DEFAULT_WIDTH, customStyle = {}, formatter) => ({
  Header: header,
  accessor,
  disableGroupBy: true,
  disableSortBy: true,
  align: 'center',
  width,
  columnStyle: { ...STYLES.header, ...customStyle },
  ...(formatter && {
    Cell: ({ value }) => formatter(value)
  })
});

const createHiddenColumn = (accessor, header, width = DEFAULT_WIDTH) =>
  createBaseColumn(accessor, header, width, { color: '#bdbdbd' })

const createColumnGroup = (header, columns, width, customStyle = {}) => ({
  Header: header,
  align: 'center',
  width,
  columnStyle: { ...STYLES.header, ...customStyle },
  columns
});

const projectedColumns = [
  createBaseColumn('projCheckCount', 'Check Count'),
  createBaseColumn('projAvgCheck', 'Avg Check', undefined, {}, formatCurrency),
  createBaseColumn('projSales', 'Sales'),
  createBaseColumn('theoFOH', 'Theo FOH'),
  createBaseColumn('theoBOH', 'Theo BOH'),
  createBaseColumn('projSPLH', 'SPLH', 50)
];

const scheduledColumns = [
  createBaseColumn('schedFOH', 'Sched FOH'),
  createBaseColumn('schedBOH', 'Sched BOH'),
  createBaseColumn('schedSPLH', 'SPLH', 50)
];

const actualColumns = [
  createBaseColumn('actCheckCount', 'Check Count'),
  createBaseColumn('actAvgCheck', 'Avg Check'),
  createBaseColumn('actSales', 'Sales'),
  createBaseColumn('adjTheoFOH', 'Adj Theo FOH'),
  createBaseColumn('adjTheoBOH', 'Adj Theo BOH'),
  createBaseColumn('actFOH', 'Act FOH'),
  createBaseColumn('actBOH', 'Act BOH'),
  createBaseColumn('fohActVsTheo', 'FOH Act - Theo'),
  createBaseColumn('bohActVsTheo', 'BOH Act - Theo'),
  createBaseColumn('theoSPLH', 'Total Theo SPLH'),
  createBaseColumn('actSPLH', 'Total Act SPLH')
];

const trainingColumns = [
  createBaseColumn('trainingFOH', 'FOH'),
  createBaseColumn('trainingBOH', 'BOH')
];

const createNotesCell = (openAddNoteDialog) => ({ value, row }) => {
  if (row.original.day !== '' && 
      (row.original.theoFOH === '' || 
      row.original.theoFOH === undefined)) {
    return (
      <IconButton
        aria-label='Add Note'
        color='inherit'
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        onClick={() => openAddNoteDialog(row.original.day)}
      >
        <NoteAddIcon fontSize='small' />
      </IconButton>
    );
  }

  // Training names are display-only decoration attached to day rows; they are
  // never part of the note and never reach the note dialog or its mutation.
  const { trainingNamesFOH = [], trainingNamesBOH = [] } = row.original;

  if (trainingNamesFOH.length === 0 && trainingNamesBOH.length === 0) {
    return <div>{value}</div>;
  }

  // A blank or whitespace-only note renders nothing, so it gets no divider either.
  const hasNote = typeof value === 'string' ? value.trim().length > 0 : Boolean(value);

  const namesStyle = hasNote
    ? { ...STYLES.trainingNames, ...STYLES.trainingNamesDivider }
    : STYLES.trainingNames;

  return (
    <div>
      {hasNote ? <div>{value}</div> : null}
      <div style={namesStyle}>
        {trainingNamesFOH.length > 0 && (
          <div>{`FOH: ${trainingNamesFOH.join(', ')}`}</div>
        )}
        {trainingNamesBOH.length > 0 && (
          <div>{`BOH: ${trainingNamesBOH.join(', ')}`}</div>
        )}
      </div>
    </div>
  );
};

export const getColumns = (openAddNoteDialog) => [
  createColumnGroup('d', [
    createHiddenColumn('day', 'day')
  ], DEFAULT_WIDTH, STYLES.hiddenHeader),
  
  createColumnGroup('Projected', projectedColumns, 
    (5 * DEFAULT_WIDTH) + 50),
  
  createColumnGroup('Scheduled', scheduledColumns, 
    DEFAULT_WIDTH * 2 + 50),
  
  createColumnGroup('Actual', actualColumns, 
    (11 * DEFAULT_WIDTH)),
  
  createColumnGroup('Training', trainingColumns, 150),
  
  createColumnGroup('n', [{
    ...createBaseColumn('notes', 'Notes', 150),
    Cell: createNotesCell(openAddNoteDialog)
  }], 150, STYLES.hiddenHeader)
];

export const TABLES = {
  empty: [{ day: 'Data Loading' }],
  loading: [{ day: 'Data Loading...' }],
  noData: [{ day: 'No Data' }]
};